<template>
  <div class="member-detail-page">
    <!-- 탭 네비게이션 -->
    <div class="tab-navigation">
      <button 
        class="tab-btn"
        :class="{ active: mainTab === 'member' }"
        @click="mainTab = 'member'"
      >
        <v-icon size="18">mdi-domain</v-icon>
        <span>관리정보</span>
      </button>
      <button 
        v-if="isAdmin"
        class="tab-btn"
        :class="{ active: mainTab === 'crawl', disabled: !memberData?.m_longterm_code || !hasCrawlData }"
        :disabled="!memberData?.m_longterm_code || !hasCrawlData"
        @click="mainTab = 'crawl'"
      >
        <v-icon size="18">mdi-hospital-building</v-icon>
        <span>시설정보</span>
        <span class="tab-admin-badge">관리자전용</span>
        <span v-if="!memberData?.m_longterm_code || !hasCrawlData" class="tab-hint">연결없음</span>
      </button>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="popup-content">
      <v-window v-model="mainTab">
        <!-- ========== 관리정보 탭 ========== -->
        <v-window-item value="member">
          <div class="ltc-info-title">
            <v-icon size="18" class="mr-1">mdi-information-outline</v-icon>
            기본정보
            <button 
              v-if="!isEditingBasicInfo" 
              class="edit-btn" 
              @click="startEditBasicInfo"
            >
              <v-icon size="14">mdi-pencil</v-icon>
              수정
            </button>
          </div>
          
          <!-- 기본정보 보기 모드 -->
          <table v-if="!isEditingBasicInfo" class="ltc-info-table">
            <tbody>
              <tr>
                <th>시설명</th>
                <td colspan="3">{{ memberData?.m_name || '-' }}</td>
              </tr>
              <tr>
                <th>시설코드</th>
                <td>{{ memberData?.m_longterm_code || '-' }}</td>
                <th>사업구분</th>
                <td>
                  <div class="biz-chips-display">
                    <template v-if="memberData?.m_biz_gubun">
                      <v-chip 
                        v-for="biz in parseBizGubun(memberData.m_biz_gubun)"
                        :key="biz"
                        :color="getBizGubunColor(biz)" 
                        size="small" 
                        label 
                        variant="flat"
                        class="mr-1"
                      >{{ biz }}</v-chip>
                    </template>
                    <span v-else>-</span>
                  </div>
                </td>
              </tr>
              <tr>
                <th>사업자번호</th>
                <td>{{ memberData?.m_biz_num || '-' }}</td>
                <th>설립일</th>
                <td>{{ formatDate(memberData?.m_since) }}</td>
              </tr>
              <tr>
                <th>현원</th>
                <td>{{ memberData?.m_hyunwon || 0 }}명</td>
                <th>정원</th>
                <td>{{ memberData?.m_jungwon || 0 }}명</td>
              </tr>
              <tr>
                <th>주소</th>
                <td colspan="3">{{ memberData?.m_addr || '-' }}</td>
              </tr>
              <tr>
                <th>전화번호</th>
                <td>{{ memberData?.m_tell || '-' }}</td>
                <th>이메일</th>
                <td>{{ memberData?.m_boss_email || '-' }}</td>
              </tr>
            </tbody>
          </table>
          
          <!-- 기본정보 수정 모드 -->
          <table v-else class="ltc-info-table edit-mode">
            <tbody>
              <tr>
                <th>시설명 <span class="required">*</span></th>
                <td colspan="3">
                  <input 
                    v-model="basicInfoForm.m_name" 
                    type="text" 
                    class="native-input full-width"
                    placeholder="시설명을 입력하세요"
                  />
                </td>
              </tr>
              <tr>
                <th>시설코드</th>
                <td>
                  <input 
                    v-model="basicInfoForm.m_longterm_code" 
                    type="text" 
                    class="native-input compact"
                    placeholder="시설코드"
                  />
                </td>
                <th>사업구분 <span class="required">*</span></th>
                <td>
                  <div class="biz-gubun-checkboxes">
                    <label 
                      v-for="biz in bizGubunOptions" 
                      :key="biz.bg_name"
                      class="biz-checkbox-item"
                      :class="{ 
                        checked: basicInfoForm.m_biz_gubun_list.includes(biz.bg_name),
                        disabled: isBizGubunDisabled(biz.bg_exclusive_group)
                      }"
                    >
                      <input 
                        type="checkbox" 
                        :value="biz.bg_name"
                        :checked="basicInfoForm.m_biz_gubun_list.includes(biz.bg_name)"
                        :disabled="isBizGubunDisabled(biz.bg_exclusive_group)"
                        @change="toggleBizGubun(biz.bg_name, biz.bg_exclusive_group)"
                      />
                      <span 
                        class="checkbox-label"
                        :style="{ 
                          '--biz-color': biz.bg_color,
                          backgroundColor: basicInfoForm.m_biz_gubun_list.includes(biz.bg_name) ? biz.bg_color : 'transparent',
                          color: basicInfoForm.m_biz_gubun_list.includes(biz.bg_name) ? 'white' : biz.bg_color,
                          borderColor: biz.bg_color,
                          opacity: isBizGubunDisabled(biz.bg_exclusive_group) ? 0.4 : 1
                        }"
                      >{{ biz.bg_name }}</span>
                    </label>
                  </div>
                </td>
              </tr>
              <tr>
                <th>사업자번호</th>
                <td>
                  <input 
                    v-model="basicInfoForm.m_biz_num" 
                    type="text" 
                    class="native-input compact"
                    placeholder="000-00-00000"
                  />
                </td>
                <th>설립일</th>
                <td>
                  <YmdDateInput v-model="basicInfoForm.m_since" class="native-input compact" />
                </td>
              </tr>
              <tr>
                <th>현원</th>
                <td>
                  <div class="number-input-wrap">
                    <input 
                      v-model.number="basicInfoForm.m_hyunwon" 
                      type="number" 
                      class="native-input compact"
                      min="0"
                    />
                    <span class="unit">명</span>
                  </div>
                </td>
                <th>정원</th>
                <td>
                  <div class="number-input-wrap">
                    <input 
                      v-model.number="basicInfoForm.m_jungwon" 
                      type="number" 
                      class="native-input compact"
                      min="0"
                    />
                    <span class="unit">명</span>
                  </div>
                </td>
              </tr>
              <tr>
                <th>주소</th>
                <td colspan="3">
                  <input 
                    v-model="basicInfoForm.m_addr" 
                    type="text" 
                    class="native-input full-width"
                    placeholder="주소를 입력하세요"
                  />
                </td>
              </tr>
              <tr>
                <th>전화번호</th>
                <td>
                  <input 
                    v-model="basicInfoForm.m_tell" 
                    type="tel" 
                    class="native-input compact"
                    placeholder="000-0000-0000"
                  />
                </td>
                <th>이메일</th>
                <td>
                  <input 
                    v-model="basicInfoForm.m_boss_email" 
                    type="email" 
                    class="native-input compact"
                    placeholder="email@example.com"
                  />
                </td>
              </tr>
              <tr>
                <td colspan="4" class="edit-actions-cell">
                  <button 
                    class="save-btn" 
                    @click="saveBasicInfo" 
                    :disabled="savingBasicInfo"
                  >
                    <v-progress-circular v-if="savingBasicInfo" indeterminate size="14" width="2" class="mr-1" />
                    <v-icon v-else size="14" class="mr-1">mdi-check</v-icon>
                    저장
                  </button>
                  <button class="cancel-btn" @click="cancelEditBasicInfo">
                    취소
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 로그인 정보 섹션 -->
          <div class="ltc-info-title" style="margin-top:24px;">
            <v-icon size="18" class="mr-1">mdi-lock-outline</v-icon>
            로그인 정보
            <button 
              v-if="!isEditingCredentials" 
              class="edit-btn" 
              @click="startEditCredentials"
            >
              <v-icon size="14">mdi-pencil</v-icon>
              수정
            </button>
          </div>
          <table class="ltc-info-table">
            <tbody>
              <tr>
                <th>아이디 (ID)</th>
                <td>
                  <template v-if="isEditingCredentials">
                    <div class="id-edit-row">
                      <input 
                        v-model="credentialsForm.m_id" 
                        type="text" 
                        class="native-input compact"
                        :class="{ 'is-valid': idCheckState.checked && idCheckState.available, 'is-invalid': idCheckState.checked && !idCheckState.available }"
                        placeholder="아이디"
                        @input="onIdChange"
                      />
                      <button 
                        type="button" 
                        class="check-id-btn" 
                        @click="checkIdDuplicate"
                        :disabled="!credentialsForm.m_id.trim() || idCheckLoading || !isIdChanged"
                      >
                        <v-progress-circular v-if="idCheckLoading" indeterminate size="12" width="2" />
                        <template v-else>중복확인</template>
                      </button>
                    </div>
                    <span v-if="idCheckState.checked && idCheckState.available" class="id-check-hint success">
                      <v-icon size="12">mdi-check-circle</v-icon> 사용 가능
                    </span>
                    <span v-else-if="idCheckState.checked && !idCheckState.available" class="id-check-hint error">
                      <v-icon size="12">mdi-alert-circle</v-icon> 사용 불가
                    </span>
                    <span v-else-if="isIdChanged" class="id-check-hint warning">
                      <v-icon size="12">mdi-alert</v-icon> 중복확인 필요
                    </span>
                  </template>
                  <template v-else>
                    {{ memberData?.m_id || '-' }}
                  </template>
                </td>
                <th>비밀번호 (PW)</th>
                <td>
                  <template v-if="isEditingCredentials">
                    <input 
                      v-model="credentialsForm.m_pw" 
                      type="text" 
                      class="native-input compact"
                      placeholder="비밀번호"
                    />
                  </template>
                  <template v-else>
                    <div class="password-display">
                      <span class="password-value">{{ showPassword ? memberData?.m_pw : maskedPassword }}</span>
                      <button 
                        class="toggle-pw-btn" 
                        @click="showPassword = !showPassword"
                        :title="showPassword ? '비밀번호 숨기기' : '비밀번호 보기'"
                      >
                        <v-icon size="16">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                      </button>
                    </div>
                  </template>
                </td>
              </tr>
              <tr v-if="isEditingCredentials">
                <td colspan="4" class="edit-actions-cell">
                  <button 
                    class="save-btn" 
                    @click="saveCredentials" 
                    :disabled="savingCredentials || (isIdChanged && (!idCheckState.checked || !idCheckState.available))"
                  >
                    <v-icon size="14" class="mr-1">mdi-check</v-icon>
                    저장
                  </button>
                  <button class="cancel-btn" @click="cancelEditCredentials">
                    취소
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="ltc-info-title" style="margin-top:24px;">
            <v-icon size="18" class="mr-1">mdi-account-outline</v-icon>
            대표자 정보
            <button 
              v-if="!isEditingBossInfo" 
              class="edit-btn" 
              @click="startEditBossInfo"
            >
              <v-icon size="14">mdi-pencil</v-icon>
              수정
            </button>
          </div>
          
          <!-- 대표자 정보 보기 모드 -->
          <table v-if="!isEditingBossInfo" class="ltc-info-table">
            <tbody>
              <tr>
                <th>대표자명</th>
                <td>{{ memberData?.m_boss_name || '-' }}</td>
                <th>대표 연락처</th>
                <td>{{ memberData?.m_boss_phone || '-' }}</td>
              </tr>
            </tbody>
          </table>
          
          <!-- 대표자 정보 수정 모드 -->
          <table v-else class="ltc-info-table edit-mode">
            <tbody>
              <tr>
                <th>대표자명</th>
                <td>
                  <input 
                    v-model="bossInfoForm.m_boss_name" 
                    type="text" 
                    class="native-input compact"
                    placeholder="대표자명"
                  />
                </td>
                <th>대표 연락처</th>
                <td>
                  <input 
                    v-model="bossInfoForm.m_boss_phone" 
                    type="tel" 
                    class="native-input compact"
                    placeholder="000-0000-0000"
                  />
                </td>
              </tr>
              <tr>
                <td colspan="4" class="edit-actions-cell">
                  <button 
                    class="save-btn" 
                    @click="saveBossInfo" 
                    :disabled="savingBossInfo"
                  >
                    <v-progress-circular v-if="savingBossInfo" indeterminate size="14" width="2" class="mr-1" />
                    <v-icon v-else size="14" class="mr-1">mdi-check</v-icon>
                    저장
                  </button>
                  <button class="cancel-btn" @click="cancelEditBossInfo">
                    취소
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="ltc-info-title" style="margin-top:24px;">
            <v-icon size="18" class="mr-1">mdi-account-tie-outline</v-icon>
            담당 권한
          </div>
          <table class="ltc-info-table">
            <tbody>
              <tr>
                <th>임금담당</th>
                <td colspan="3">{{ memberData?.wage_assignee_names || '-' }}</td>
              </tr>
              <tr>
                <th>회계담당</th>
                <td colspan="3">{{ memberData?.account_assignee_names || '-' }}</td>
              </tr>
            </tbody>
          </table>

          <div class="ltc-info-title" style="margin-top:24px;">
            <v-icon size="18" class="mr-1">mdi-clock-outline</v-icon>
            등록/수정 이력
          </div>
          <table class="ltc-info-table">
            <tbody>
              <tr>
                <th>등록일</th>
                <td>{{ formatDateTime(memberData?.m_wdate) }}</td>
                <th>최근 수정일</th>
                <td>{{ formatDateTime(memberData?.m_rdate) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- 공동인증서 관리 섹션 -->
          <div class="ltc-info-title" style="margin-top:24px;">
            <v-icon size="18" class="mr-1">mdi-certificate</v-icon>
            공동인증서
            <button 
              v-if="certInfo && !certLoading" 
              class="edit-btn" 
              @click="openCertModal"
            >
              <v-icon size="14">mdi-pencil</v-icon>
              수정
            </button>
          </div>
          <div class="cert-section">
            <!-- 인증서 등록 상태 -->
            <div v-if="certLoading" class="cert-loading">
              <v-progress-circular indeterminate size="20" width="2" />
              <span>인증서 정보 확인 중...</span>
            </div>
            
            <!-- 인증서 등록됨 -->
            <div v-else-if="certInfo" class="cert-registered">
              <table class="ltc-info-table">
                <tbody>
                  <tr>
                    <th>발급자</th>
                    <td>{{ certInfo.cic_cert_name || '-' }}</td>
                    <th>만료일</th>
                    <td>
                      <span :class="{ 'cert-date-alert': isExpired || isExpiringSoon }">
                        {{ formatDate(certInfo.cic_expire_date) }}
                        <span v-if="isExpired" class="expire-warning">만료되었습니다</span>
                        <span v-else-if="isExpiringSoon" class="expire-warning">(만료 임박)</span>
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>등록일</th>
                    <td>{{ formatDateTime(certInfo.cic_wdate) }}</td>
                    <th>마지막 사용</th>
                    <td>{{ certInfo.cic_last_used ? formatDateTime(certInfo.cic_last_used) : '사용 기록 없음' }}</td>
                  </tr>
                </tbody>
              </table>
              
              <!-- 슈퍼바이저: IP 관리 -->
              <div v-if="isSuperAdmin" class="ip-management-section">
                <button class="ip-toggle-btn" @click="toggleIpManagement">
                  <v-icon size="16">{{ showIpManagement ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  접근 허용 IP 관리
                  <span class="ip-count">({{ allowedIPs.length }}개)</span>
                </button>
                
                <div v-if="showIpManagement" class="ip-management-content">
                  <div class="ip-header">
                    <div class="my-ip-info">
                      <span class="my-ip-label">현재 IP:</span>
                      <span class="my-ip-value">{{ myIP || '조회중...' }}</span>
                    </div>
                    <v-btn size="small" variant="flat" class="btn-register" @click="openAddIpDialog">
                      <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                      IP 추가
                    </v-btn>
                  </div>

                  <div v-if="loadingIPs" class="ip-loading">
                    <v-progress-circular indeterminate size="24" />
                  </div>

                  <div v-else-if="allowedIPs.length === 0" class="ip-empty">
                    등록된 허용 IP가 없습니다.
                  </div>

                  <div v-else class="ip-list">
                    <div 
                      v-for="ip in allowedIPs" 
                      :key="ip.ccai_idx" 
                      class="ip-item"
                      :class="{ inactive: !ip.ccai_is_active }"
                    >
                      <div class="ip-info">
                        <span class="ip-address">{{ ip.ccai_ip }}</span>
                        <span v-if="ip.ccai_description" class="ip-desc">{{ ip.ccai_description }}</span>
                      </div>
                      <div class="ip-actions">
                        <v-switch
                          :model-value="!!ip.ccai_is_active"
                          hide-details
                          density="compact"
                          color="primary"
                          @update:model-value="toggleAllowedIP(ip, $event ?? false)"
                        />
                        <v-btn icon variant="text" size="x-small" color="error" @click="deleteAllowedIP(ip)">
                          <v-icon size="16">mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 인증서 미등록 -->
            <div v-else class="cert-not-registered">
              <div class="cert-empty-icon">
                <v-icon size="40" color="grey-lighten-1">mdi-certificate-outline</v-icon>
              </div>
              <p class="cert-empty-text">등록된 공동인증서가 없습니다</p>
              <p class="cert-empty-hint">공동인증서를 등록하면 자동가져오기 기능을 이용할 수 있습니다</p>
              <button class="cert-upload-btn" @click="openCertModal">
                <v-icon size="16">mdi-upload</v-icon>
                인증서 등록
              </button>
            </div>
          </div>

          <!-- IP 추가 다이얼로그 (슈퍼바이저 전용) -->
          <v-dialog v-model="showAddIpDialog" max-width="400">
            <v-card>
              <v-card-title class="ip-dialog-title">
                IP 추가
                <button
                  type="button"
                  class="ip-dialog-close"
                  aria-label="닫기"
                  :disabled="addingIP"
                  @click="showAddIpDialog = false"
                >
                  <v-icon size="18">mdi-close</v-icon>
                </button>
              </v-card-title>
              <v-card-text>
                <div class="ip-input-row">
                  <v-text-field
                    v-model="newIP.ip"
                    label="IP 주소"
                    placeholder="예: 123.456.789.0"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                  <v-btn
                    variant="outlined"
                    size="small"
                    class="apply-my-ip-btn"
                    @click="applyMyIP"
                    :disabled="!myIP"
                  >
                    <v-icon size="16" class="mr-1">mdi-crosshairs-gps</v-icon>
                    현재IP
                  </v-btn>
                </div>
                <v-text-field
                  v-model="newIP.description"
                  label="설명 (선택)"
                  placeholder="예: 본사 PC, 김대리 노트북"
                  variant="outlined"
                  density="compact"
                  class="mt-3"
                />
              </v-card-text>
              <v-card-actions>
                <v-btn variant="text" class="btn-cancel" @click="showAddIpDialog = false">취소</v-btn>
                <v-spacer />
                <v-btn variant="flat" class="btn-save" @click="addAllowedIP" :loading="addingIP">추가</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- 인증서 업로드 모달 (NPKI 원본 파일 방식) -->
          <v-dialog v-model="showCertUploadModal" max-width="600" persistent>
            <div class="cert-upload-modal">
              <div class="modal-header modal-header--dark">
                <h3>{{ certInfo ? '공동인증서 관리' : '공동인증서 등록' }}</h3>
                <button class="close-btn" @click="closeCertUploadModal">
                  <v-icon>mdi-close</v-icon>
                </button>
              </div>
              <div class="modal-body">
                <!-- 기존 인증서가 있을 때: 모드 선택 탭 -->
                <div v-if="certInfo" class="cert-mode-tabs">
                  <button 
                    :class="['mode-tab', { active: certEditMode === 'password' }]"
                    @click="certEditMode = 'password'"
                  >
                    <v-icon size="16" class="mr-1">mdi-key</v-icon>
                    비밀번호만 변경
                  </button>
                  <button 
                    :class="['mode-tab', { active: certEditMode === 'full' }]"
                    @click="certEditMode = 'full'"
                  >
                    <v-icon size="16" class="mr-1">mdi-certificate</v-icon>
                    인증서 전체 변경
                  </button>
                </div>

                <!-- 비밀번호만 변경 모드 -->
                <div v-if="certInfo && certEditMode === 'password'" class="password-only-mode">
                  <div class="current-cert-info">
                    <div class="info-label">현재 등록된 인증서</div>
                    <div class="info-value">{{ certInfo.cic_cert_name || '알 수 없음' }}</div>
                    <div class="info-sub">만료일: {{ formatDate(certInfo.cic_expire_date) }}</div>
                  </div>
                  
                  <div class="upload-field">
                    <label>새 비밀번호 <span class="required">*</span></label>
                    <div class="password-input-wrap">
                      <input 
                        v-model="newCertPassword" 
                        :type="showNewCertPassword ? 'text' : 'password'" 
                        class="native-input full-width"
                        placeholder="새 인증서 비밀번호를 입력하세요"
                      />
                      <button 
                        type="button" 
                        class="toggle-password-btn" 
                        @click="showNewCertPassword = !showNewCertPassword"
                      >
                        <v-icon size="18">{{ showNewCertPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                      </button>
                    </div>
                    <div class="password-hint">
                      <v-icon size="12" color="warning" class="mr-1">mdi-alert</v-icon>
                      저장 시 비밀번호가 이 인증서와 일치하는지 자동으로 확인됩니다.
                    </div>
                  </div>
                </div>

                <!-- 인증서 전체 변경 모드 (또는 신규 등록) -->
                <template v-if="!certInfo || certEditMode === 'full'">
                  <!-- 안내사항 -->
                  <div class="cert-guide npki-guide">
                  <div class="guide-header">
                    <v-icon size="20" color="primary">mdi-information</v-icon>
                    <strong>인증서 폴더 한 번에 올리기</strong>
                  </div>
                  <div class="guide-steps">
                    <div class="guide-step">
                      <div class="step-number">1</div>
                      <div class="step-content">
                        <span class="step-title">아래 「인증서 폴더 선택」 버튼 클릭</span>
                        <span class="step-desc">폴더를 고르는 창이 열립니다.</span>
                      </div>
                    </div>
                    <div class="guide-step">
                      <div class="step-number">2</div>
                      <div class="step-content">
                        <span class="step-title">열린 창 주소창에 경로 붙여넣기</span>
                        <span class="step-desc">경로를 복사한 뒤 창 위쪽 주소창에 붙여넣고 Enter</span>
                        <div class="path-box">
                          <code>%USERPROFILE%\AppData\LocalLow\NPKI</code>
                          <button class="copy-path-btn" @click="copyNpkiPath" type="button">
                            <v-icon size="14">mdi-content-copy</v-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="guide-step">
                      <div class="step-number">3</div>
                      <div class="step-content">
                        <span class="step-title">내 인증서 폴더까지 이동</span>
                        <span class="step-desc">
                          인증기관 폴더(예: yessign, SignKorea) → <b>USER</b> →
                          본인 인증서 폴더(cn=… 형태)를 선택하고 「폴더 선택」 확인
                        </span>
                        <span class="step-desc step-desc--tip">
                          인증서가 여러 개면 USER를 통째로 고르지 말고, 사용할 인증서 폴더 하나만 선택하세요.
                        </span>
                      </div>
                    </div>
                    <div class="guide-step">
                      <div class="step-number">4</div>
                      <div class="step-content">
                        <span class="step-title">자동 인식 확인</span>
                        <span class="step-desc">
                          <span class="file-badge cert">signCert.der</span> /
                          <span class="file-badge key">signPri.key</span> 가 자동으로 채워집니다.
                          그 다음 비밀번호만 입력하면 됩니다.
                        </span>
                        <span class="step-desc step-desc--tip">
                          USB·다른 PC에 복사해 둔 인증서도 같은 방법으로 폴더만 선택하면 됩니다.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 파일 선택 영역 (폴더 선택 + 드래그 앤 드롭) -->
                <div class="upload-field">
                  <label>인증서 파일 <span class="required">*</span></label>
                  <div class="cert-pick-actions">
                    <button type="button" class="cert-folder-pick-btn" @click="triggerCertFolderInput">
                      <v-icon size="18" class="mr-1">mdi-folder-open</v-icon>
                      인증서 폴더 선택
                    </button>
                    <button type="button" class="cert-files-pick-btn" @click="triggerCertFilesInput">
                      <v-icon size="16" class="mr-1">mdi-file-multiple-outline</v-icon>
                      파일 2개 직접 선택
                    </button>
                  </div>
                  <input
                    type="file"
                    ref="certFolderInputRef"
                    style="display: none"
                    @change="onCertFolderChange"
                  />
                  <div 
                    class="file-drop-zone"
                    :class="{ 'drag-over': isDragOver, 'has-files': signCertFile && signPriFile }"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                  >
                    <input 
                      type="file" 
                      ref="certFilesInputRef"
                      accept=".der,.key"
                      multiple
                      @change="onCertFilesChange"
                      style="display: none"
                    />
                    <!-- 파일 미선택 시: 안내 메시지 -->
                    <div v-if="!signCertFile && !signPriFile" class="drop-zone-content">
                      <v-icon size="32" :color="isDragOver ? 'primary' : 'grey-lighten-1'">
                        {{ isDragOver ? 'mdi-file-download' : 'mdi-folder-upload-outline' }}
                      </v-icon>
                      <span class="drop-text">
                        위 버튼으로 폴더를 선택하거나, 파일을 여기에 드래그하세요
                      </span>
                      <span class="drop-hint">권장: 인증서 폴더 통째 선택 → der/key 자동 인식</span>
                    </div>
                    <!-- 파일 선택됨: 파일 목록 표시 -->
                    <div v-else class="drop-zone-files">
                      <div class="file-item" :class="{ 'has-file': signCertFile }">
                        <v-icon v-if="signCertFile" size="16" color="success">mdi-check-circle</v-icon>
                        <v-icon v-else size="16" color="grey-lighten-1">mdi-close-circle</v-icon>
                        <span class="file-badge cert">signCert.der</span>
                        <span class="file-status">{{ signCertFile ? signCertFileName : '선택 안됨' }}</span>
                      </div>
                      <div class="file-item" :class="{ 'has-file': signPriFile }">
                        <v-icon v-if="signPriFile" size="16" color="success">mdi-check-circle</v-icon>
                        <v-icon v-else size="16" color="grey-lighten-1">mdi-close-circle</v-icon>
                        <span class="file-badge key">signPri.key</span>
                        <span class="file-status">{{ signPriFile ? signPriFileName : '선택 안됨' }}</span>
                      </div>
                      <span v-if="pickedCertFolderHint" class="change-hint">폴더: {{ pickedCertFolderHint }}</span>
                      <span class="change-hint">위에서 다시 선택하면 변경됩니다</span>
                    </div>
                  </div>
                </div>

                <!-- 인증서 비밀번호 -->
                <div class="upload-field">
                  <label>인증서 비밀번호 <span class="required">*</span></label>
                  <div class="password-input-wrap">
                    <input 
                      v-model="certPassword" 
                      :type="showCertPassword ? 'text' : 'password'" 
                      class="native-input full-width"
                      placeholder="인증서 비밀번호를 입력하세요"
                    />
                    <button 
                      type="button" 
                      class="toggle-password-btn" 
                      @click="showCertPassword = !showCertPassword"
                      :title="showCertPassword ? '비밀번호 숨기기' : '비밀번호 보기'"
                    >
                      <v-icon size="18">{{ showCertPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    </button>
                  </div>
                  <div class="password-hint">
                    <v-icon size="12" color="warning" class="mr-1">mdi-alert</v-icon>
                    등록 시 비밀번호가 이 인증서와 일치하는지 자동으로 확인됩니다.
                  </div>
                </div>

                <!-- 인증서 정보 (DER에서 추출) -->
                <div v-if="extractedCertInfo" class="cert-info-box">
                  <div class="cert-info-header">
                    <v-icon size="16" color="success">mdi-certificate</v-icon>
                    <span>인증서 정보</span>
                  </div>
                  <div class="cert-info-row">
                    <span class="cert-info-label">발급자</span>
                    <span class="cert-info-value">{{ extractedCertInfo.subject }}</span>
                  </div>
                  <div class="cert-info-row">
                    <span class="cert-info-label">만료일</span>
                    <span class="cert-info-value">{{ extractedCertInfo.expireDate }}</span>
                  </div>
                </div>

                <div class="cert-notice">
                  <v-icon size="14" color="warning">mdi-shield-lock</v-icon>
                  <span>업로드된 인증서는 AES-256으로 암호화되어 안전하게 저장됩니다.</span>
                </div>
                </template>
              </div>
              <div class="modal-footer">
                <!-- 기존 인증서가 있을 때만 삭제 버튼 표시 (왼쪽) -->
                <button 
                  v-if="certInfo && certEditMode === 'full'" 
                  class="delete-btn" 
                  @click="confirmDeleteCert"
                  :disabled="certUploading || certPasswordUpdating"
                >
                  <v-icon size="14" class="mr-1">mdi-delete</v-icon>
                  삭제
                </button>
                <div class="footer-spacer"></div>
                <button class="cancel-btn" @click="closeCertUploadModal" :disabled="certUploading || certPasswordUpdating">
                  취소
                </button>
                
                <!-- 비밀번호만 변경 모드 -->
                <button 
                  v-if="certInfo && certEditMode === 'password'"
                  class="save-btn" 
                  @click="updateCertPasswordOnly" 
                  :disabled="!newCertPassword.trim() || certPasswordUpdating"
                >
                  <v-progress-circular v-if="certPasswordUpdating" indeterminate size="14" width="2" class="mr-1" />
                  <v-icon v-else size="14" class="mr-1">mdi-content-save</v-icon>
                  {{ certPasswordUpdating ? '저장 중...' : '비밀번호 변경' }}
                </button>
                
                <!-- 인증서 전체 변경/등록 모드 -->
                <button 
                  v-else
                  class="save-btn" 
                  @click="uploadCert" 
                  :disabled="!canUploadCert || certUploading"
                >
                  <v-progress-circular v-if="certUploading" indeterminate size="14" width="2" class="mr-1" />
                  <v-icon v-else size="14" class="mr-1">mdi-upload</v-icon>
                  {{ certUploading ? '업로드 중...' : (certInfo ? '인증서 변경' : '등록') }}
                </button>
              </div>
            </div>
          </v-dialog>

          <!-- 인증서 삭제 확인 다이얼로그 -->
          <v-dialog v-model="showDeleteCertDialog" max-width="400">
            <div class="delete-confirm-dialog">
              <button
                type="button"
                class="dialog-x-close"
                aria-label="닫기"
                :disabled="certDeleting"
                @click="showDeleteCertDialog = false"
              >
                <v-icon size="18">mdi-close</v-icon>
              </button>
              <div class="dialog-icon">
                <v-icon size="48" color="error">mdi-alert-circle</v-icon>
              </div>
              <h3>인증서 삭제</h3>
              <p>등록된 공동인증서를 삭제하시겠습니까?<br/>삭제 후에는 복구할 수 없습니다.</p>
              <div class="dialog-actions">
                <button class="cancel-btn" @click="showDeleteCertDialog = false">취소</button>
                <button class="delete-btn" @click="deleteCert" :disabled="certDeleting">
                  <v-progress-circular v-if="certDeleting" indeterminate size="14" width="2" class="mr-1" />
                  삭제
                </button>
              </div>
            </div>
          </v-dialog>

          <!-- 희망이음 전용 공동인증서 (개인 인증서 + 희망이음 id/pw) -->
          <HopeCertSection :m-code="memberData?.m_code" />
        </v-window-item>

        <!-- ========== 시설정보 탭 (관리자 전용) ========== -->
        <v-window-item v-if="isAdmin" value="crawl">
          <CrawlDetailContent 
            v-if="mainTab === 'crawl' && memberData?.m_longterm_code" 
            :cc-number="memberData.m_longterm_code" 
            :embedded="true"
          />
        </v-window-item>
      </v-window>
    </div>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar" :timeout="2000" :color="snackbarColor" location="bottom">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, watch, computed } from 'vue';
import { usePageTabNav } from '@/composables/usePageTabNav';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import { useStore } from 'vuex';
import { crawlApi, memberApi, authApi, certApi } from '@/services/api';
import CrawlDetailContent from '@/pages/facility/CrawlDetailPopup.vue';
import HopeCertSection from '@/pages/member/detail/HopeCertSection.vue';
import forge from 'node-forge';
import { pickNpkiCertFiles, NPKI_PATH_HINT } from '@/utils/pickNpkiCertFiles';

const store = useStore();
const roleLevel = computed(() => Number(store.getters.roleLevel || 0));
const isAdmin = computed(() => roleLevel.value >= 60);
const isSuperAdmin = computed(() => roleLevel.value >= 100);

// 사업구분 타입 정의
interface BizGubunOption {
  bg_name: string;
  bg_color: string;
  bg_exclusive_group: string | null;
}

// 레이아웃에서 제공하는 데이터
const memberData = inject<any>('memberData');
const bizGubunColorMap = inject<any>('bizGubunColorMap');

// 탭 상태
const mainTab = ref('member');

watch(isAdmin, (admin) => {
  if (!admin && mainTab.value === 'crawl') {
    mainTab.value = 'member';
  }
}, { immediate: true });

// 크롤링 데이터 연결 여부 확인용
const hasCrawlData = ref(false);

const infoPageTabs = computed(() => {
  const tabs: { key: string; title: string; disabled?: boolean }[] = [
    { key: 'member', title: '관리정보' },
  ];
  if (isAdmin.value) {
    tabs.push({
      key: 'crawl',
      title: '시설정보',
      disabled: !memberData?.value?.m_longterm_code || !hasCrawlData.value,
    });
  }
  return tabs;
});
usePageTabNav('facility-info', infoPageTabs, mainTab, (key) => {
  mainTab.value = key;
});

// 비밀번호 표시 상태
const showPassword = ref(false);

// 사업구분 옵션 목록
const bizGubunOptions = ref<BizGubunOption[]>([]);

// 기본정보 수정 상태
const isEditingBasicInfo = ref(false);
const savingBasicInfo = ref(false);
const basicInfoForm = ref({
  m_name: '',
  m_longterm_code: '',
  m_biz_gubun_list: [] as string[],
  m_biz_num: '',
  m_since: '',
  m_hyunwon: 0,
  m_jungwon: 0,
  m_addr: '',
  m_tell: '',
  m_boss_email: ''
});

// 로그인 정보 수정 상태
const isEditingCredentials = ref(false);
const savingCredentials = ref(false);
const idCheckLoading = ref(false);
const originalId = ref(''); // 원래 ID (변경 여부 확인용)
const credentialsForm = ref({
  m_id: '',
  m_pw: ''
});
const idCheckState = ref({
  checked: false,
  available: false
});

// 대표자 정보 수정 상태
const isEditingBossInfo = ref(false);
const savingBossInfo = ref(false);
const bossInfoForm = ref({
  m_boss_name: '',
  m_boss_phone: ''
});

// ID가 변경되었는지 확인
const isIdChanged = computed(() => {
  return credentialsForm.value.m_id.trim() !== originalId.value;
});

// 스낵바
const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');

// 인증서 관련 상태 (NPKI 원본 파일 방식)
const certLoading = ref(false);
const certInfo = ref<any>(null);
const showCertUploadModal = ref(false);
const showDeleteCertDialog = ref(false);
const certUploading = ref(false);
const certDeleting = ref(false);
const certPassword = ref('');
const showCertPassword = ref(false);
const certEditMode = ref<'password' | 'full'>('password');  // 비밀번호만 변경 / 전체 변경
const newCertPassword = ref('');
const showNewCertPassword = ref(false);
const certPasswordUpdating = ref(false);

// NPKI 파일 (signCert.der + signPri.key)
const signCertFile = ref<File | null>(null);
const signPriFile = ref<File | null>(null);
const signCertFileName = ref('');
const signPriFileName = ref('');
const certFilesInputRef = ref<HTMLInputElement | null>(null);
const certFolderInputRef = ref<HTMLInputElement | null>(null);
const pickedCertFolderHint = ref('');
const extractedCertInfo = ref<{ subject: string; expireDate: string } | null>(null);
const isDragOver = ref(false);

// IP 관리 상태 (슈퍼바이저 전용)
const showIpManagement = ref(false);
const allowedIPs = ref<any[]>([]);
const loadingIPs = ref(false);
const showAddIpDialog = ref(false);
const addingIP = ref(false);
const newIP = ref({ ip: '', description: '' });
const myIP = ref('');

// NPKI 경로 복사 (환경변수 사용 - 탐색기에서 자동 변환됨)
const copyNpkiPath = async () => {
  try {
    await navigator.clipboard.writeText(NPKI_PATH_HINT);
    snackbarText.value = '경로가 복사되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch {
    // Fallback for non-secure contexts
    const textarea = document.createElement('textarea');
    textarea.value = NPKI_PATH_HINT;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    snackbarText.value = '경로가 복사되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  }
};

const enableDirectoryPicker = (el: HTMLInputElement | null) => {
  if (!el) return;
  // Chromium/Edge(Windows NPKI): 폴더 선택
  (el as HTMLInputElement & { webkitdirectory?: boolean }).webkitdirectory = true;
  el.setAttribute('webkitdirectory', '');
  el.setAttribute('directory', '');
  el.multiple = true;
};

const triggerCertFolderInput = () => {
  enableDirectoryPicker(certFolderInputRef.value);
  certFolderInputRef.value?.click();
};

// 파일 2개 직접 선택
const triggerCertFilesInput = () => {
  certFilesInputRef.value?.click();
};

// 드래그 앤 드롭 이벤트 핸들러
const onDragOver = () => {
  isDragOver.value = true;
};

const onDragLeave = () => {
  isDragOver.value = false;
};

const onDrop = (event: DragEvent) => {
  isDragOver.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    processFiles(files);
  }
};

// 인증서 업로드 가능 여부 (두 파일 + 비밀번호)
const canUploadCert = computed(() => {
  return signCertFile.value && signPriFile.value && certPassword.value.trim();
});

const certExpireDayDiff = (raw: unknown) => {
  if (!raw) return null;
  const expireDate = new Date(raw as string);
  if (Number.isNaN(expireDate.getTime())) return null;
  const expire = new Date(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate());
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((expire.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
};

const isExpired = computed(() => {
  const diffDays = certExpireDayDiff(certInfo.value?.cic_expire_date);
  return diffDays !== null && diffDays < 0;
});

// 인증서 만료 임박 여부 (30일 이내, 아직 만료 전)
const isExpiringSoon = computed(() => {
  const diffDays = certExpireDayDiff(certInfo.value?.cic_expire_date);
  return diffDays !== null && diffDays <= 30 && diffDays >= 0;
});

// 마스킹된 비밀번호
const maskedPassword = computed(() => {
  const pw = memberData?.value?.m_pw;
  if (!pw) return '-';
  return '•'.repeat(pw.length);
});

// 사업구분 색상
const getBizGubunColor = (bizGubun: string) => {
  return bizGubunColorMap?.value?.[bizGubun] || '#607d8b';
};

// 사업구분 문자열을 배열로 파싱
const parseBizGubun = (bizGubunStr: string) => {
  if (!bizGubunStr) return [];
  return bizGubunStr.split(',').map(s => s.trim()).filter(Boolean);
};

// 현재 선택된 사업구분의 exclusive_group 조회
const getSelectedExclusiveGroup = (): string | null => {
  const selected = basicInfoForm.value.m_biz_gubun_list;
  if (selected.length === 0) return null;
  
  const firstSelected = selected[0];
  const biz = bizGubunOptions.value.find(b => b.bg_name === firstSelected);
  return biz?.bg_exclusive_group || null;
};

// 특정 그룹이 비활성화되어야 하는지 확인
const isBizGubunDisabled = (exclusiveGroup: string | null): boolean => {
  const currentGroup = getSelectedExclusiveGroup();
  if (!currentGroup) return false;
  if (!exclusiveGroup) return false;
  return currentGroup !== exclusiveGroup;
};

// 사업구분 토글
const toggleBizGubun = (bizName: string, exclusiveGroup: string | null) => {
  const current = basicInfoForm.value.m_biz_gubun_list;
  const idx = current.indexOf(bizName);
  
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    const currentGroup = getSelectedExclusiveGroup();
    if (currentGroup && currentGroup !== exclusiveGroup) {
      return;
    }
    current.push(bizName);
  }
  
  // DB 순서대로 정렬
  const orderedBizList = bizGubunOptions.value.map(b => b.bg_name);
  current.sort((a, b) => orderedBizList.indexOf(a) - orderedBizList.indexOf(b));
};

// 사업구분 목록 로드
const loadBizGubunOptions = async () => {
  try {
    const response = await memberApi.getBizGubunList();
    if (response.data?.data) {
      bizGubunOptions.value = response.data.data;
    }
  } catch (error) {
    console.error('사업구분 목록 로드 오류:', error);
  }
};

// 기본정보 수정 시작
const startEditBasicInfo = () => {
  const data = memberData?.value;
  if (!data) return;
  
  basicInfoForm.value = {
    m_name: data.m_name || '',
    m_longterm_code: data.m_longterm_code || '',
    m_biz_gubun_list: parseBizGubun(data.m_biz_gubun),
    m_biz_num: data.m_biz_num || '',
    m_since: data.m_since ? formatDateForInput(data.m_since) : '',
    m_hyunwon: data.m_hyunwon || 0,
    m_jungwon: data.m_jungwon || 0,
    m_addr: data.m_addr || '',
    m_tell: data.m_tell || '',
    m_boss_email: data.m_boss_email || ''
  };
  isEditingBasicInfo.value = true;
};

// 기본정보 수정 취소
const cancelEditBasicInfo = () => {
  isEditingBasicInfo.value = false;
};

// 기본정보 저장
const saveBasicInfo = async () => {
  if (!memberData?.value?.m_code) return;
  
  // 유효성 검사
  if (!basicInfoForm.value.m_name.trim()) {
    snackbarText.value = '시설명을 입력해주세요.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }
  
  if (basicInfoForm.value.m_biz_gubun_list.length === 0) {
    snackbarText.value = '사업구분을 하나 이상 선택해주세요.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }
  
  savingBasicInfo.value = true;
  try {
    const updateData = {
      m_name: basicInfoForm.value.m_name.trim(),
      m_longterm_code: basicInfoForm.value.m_longterm_code.trim() || null,
      m_biz_gubun: basicInfoForm.value.m_biz_gubun_list.join(','),
      m_biz_num: basicInfoForm.value.m_biz_num.trim() || null,
      m_since: basicInfoForm.value.m_since || null,
      m_hyunwon: basicInfoForm.value.m_hyunwon || 0,
      m_jungwon: basicInfoForm.value.m_jungwon || 0,
      m_addr: basicInfoForm.value.m_addr.trim() || null,
      m_tell: basicInfoForm.value.m_tell.trim() || null,
      m_boss_email: basicInfoForm.value.m_boss_email.trim() || null
    };
    
    await memberApi.update(memberData.value.m_code, updateData);
    
    // 로컬 데이터 업데이트
    Object.assign(memberData.value, updateData);
    
    isEditingBasicInfo.value = false;
    snackbarText.value = '기본정보가 저장되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('기본정보 저장 오류:', error);
    snackbarText.value = error.response?.data?.message || '저장에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    savingBasicInfo.value = false;
  }
};

// 날짜를 input[type=date] 형식으로 변환
const formatDateForInput = (value: string | null | undefined) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 포맷 유틸
const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// 로그인 정보 수정 시작
const startEditCredentials = () => {
  credentialsForm.value.m_id = memberData?.value?.m_id || '';
  credentialsForm.value.m_pw = memberData?.value?.m_pw || '';
  originalId.value = memberData?.value?.m_id || '';
  idCheckState.value.checked = false;
  idCheckState.value.available = false;
  isEditingCredentials.value = true;
};

// ID 입력 변경 시 중복확인 상태 초기화
const onIdChange = () => {
  idCheckState.value.checked = false;
  idCheckState.value.available = false;
};

// ID 중복확인
const checkIdDuplicate = async () => {
  const memberId = credentialsForm.value.m_id.trim();
  if (!memberId) {
    alert('아이디를 입력해주세요.');
    return;
  }
  
  idCheckLoading.value = true;
  try {
    const response = await authApi.checkIdDuplicate(memberId, 'member');
    const isDuplicate = response.data?.data?.isDuplicate;
    
    idCheckState.value.checked = true;
    idCheckState.value.available = !isDuplicate;
  } catch (error: any) {
    console.error('ID 중복확인 오류:', error);
    alert('중복확인 중 오류가 발생했습니다.');
    idCheckState.value.checked = false;
    idCheckState.value.available = false;
  } finally {
    idCheckLoading.value = false;
  }
};

// 로그인 정보 수정 취소
const cancelEditCredentials = () => {
  isEditingCredentials.value = false;
  credentialsForm.value.m_id = '';
  credentialsForm.value.m_pw = '';
  originalId.value = '';
  idCheckState.value.checked = false;
  idCheckState.value.available = false;
};

// 로그인 정보 저장
const saveCredentials = async () => {
  if (!memberData?.value?.m_code) return;
  
  if (!credentialsForm.value.m_id.trim()) {
    snackbarText.value = '아이디를 입력해주세요.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }
  
  // ID가 변경되었으면 중복확인 필수
  if (isIdChanged.value && (!idCheckState.value.checked || !idCheckState.value.available)) {
    snackbarText.value = '아이디 중복확인을 해주세요.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }
  
  if (!credentialsForm.value.m_pw.trim()) {
    snackbarText.value = '비밀번호를 입력해주세요.';
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }
  
  savingCredentials.value = true;
  try {
    await memberApi.update(memberData.value.m_code, {
      m_id: credentialsForm.value.m_id.trim(),
      m_pw: credentialsForm.value.m_pw.trim()
    });
    
    // 로컬 데이터 업데이트
    memberData.value.m_id = credentialsForm.value.m_id.trim();
    memberData.value.m_pw = credentialsForm.value.m_pw.trim();
    
    isEditingCredentials.value = false;
    snackbarText.value = '로그인 정보가 저장되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('로그인 정보 저장 오류:', error);
    snackbarText.value = error.response?.data?.message || '저장에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    savingCredentials.value = false;
  }
};

// 대표자 정보 수정 시작
const startEditBossInfo = () => {
  const data = memberData?.value;
  if (!data) return;
  
  bossInfoForm.value = {
    m_boss_name: data.m_boss_name || '',
    m_boss_phone: data.m_boss_phone || ''
  };
  isEditingBossInfo.value = true;
};

// 대표자 정보 수정 취소
const cancelEditBossInfo = () => {
  isEditingBossInfo.value = false;
};

// 대표자 정보 저장
const saveBossInfo = async () => {
  if (!memberData?.value?.m_code) return;
  
  savingBossInfo.value = true;
  try {
    const updateData = {
      m_boss_name: bossInfoForm.value.m_boss_name.trim() || null,
      m_boss_phone: bossInfoForm.value.m_boss_phone.trim() || null
    };
    
    await memberApi.update(memberData.value.m_code, updateData);
    
    // 로컬 데이터 업데이트
    Object.assign(memberData.value, updateData);
    
    isEditingBossInfo.value = false;
    snackbarText.value = '대표자 정보가 저장되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('대표자 정보 저장 오류:', error);
    snackbarText.value = error.response?.data?.message || '저장에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    savingBossInfo.value = false;
  }
};

// 크롤링 데이터 연결 확인
const checkCrawlData = async (mCode: string) => {
  if (!mCode) {
    hasCrawlData.value = false;
    return;
  }
  
  try {
    const response = await crawlApi.getDetail(mCode);
    hasCrawlData.value = !!response.data?.data?.main;
  } catch {
    hasCrawlData.value = false;
  }
};

// memberData 변경 감지
watch(() => memberData?.value?.m_longterm_code, (newCode) => {
  if (newCode) {
    checkCrawlData(newCode);
  }
}, { immediate: true });

// 인증서 정보 로드
const loadCertInfo = async () => {
  if (!memberData?.value?.m_code) return;
  
  certLoading.value = true;
  try {
    const response = await certApi.getInfo(memberData.value.m_code);
    certInfo.value = response.data?.data || null;
    
    // 슈퍼관리자이고 인증서가 있으면 IP 목록도 로드
    if (isSuperAdmin.value && certInfo.value?.cic_idx) {
      loadAllowedIPs();
    }
  } catch (error: any) {
    if (error.response?.status !== 404) {
      console.error('인증서 정보 로드 오류:', error);
    }
    certInfo.value = null;
  } finally {
    certLoading.value = false;
  }
};

const parseDerPreview = (file: File) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      let binaryString = '';
      for (let j = 0; j < bytes.length; j++) {
        binaryString += String.fromCharCode(bytes[j]);
      }

      const asn1 = forge.asn1.fromDer(binaryString);
      const certificate = forge.pki.certificateFromAsn1(asn1);

      const decodeField = (field: any): string => {
        if (!field?.value) return '';
        try {
          const fieldBytes = new Uint8Array([...field.value].map((c: string) => c.charCodeAt(0)));
          return new TextDecoder('utf-8').decode(fieldBytes);
        } catch {
          return field.value;
        }
      };

      const cnValue = decodeField(certificate.subject.getField('CN'));
      const serialNumber = decodeField(certificate.subject.getField('serialNumber'));

      let subject = cnValue;
      if (serialNumber && !cnValue.includes(serialNumber)) {
        subject = `${cnValue}(${serialNumber})`;
      }

      const validTo = certificate.validity.notAfter;
      const expireDate = `${validTo.getFullYear()}-${String(validTo.getMonth() + 1).padStart(2, '0')}-${String(validTo.getDate()).padStart(2, '0')}`;

      extractedCertInfo.value = { subject, expireDate };
    } catch (error) {
      console.error('DER 파일 파싱 오류:', error);
      extractedCertInfo.value = null;
    }
  };
  reader.readAsArrayBuffer(file);
};

// 폴더/파일 목록에서 der+key 자동 인식
const processFiles = (files: FileList | File[]) => {
  const picked = pickNpkiCertFiles(files);
  if (!picked.ok) {
    snackbarText.value = picked.message;
    snackbarColor.value = 'error';
    snackbar.value = true;
    return;
  }

  signCertFile.value = picked.der;
  signPriFile.value = picked.key;
  signCertFileName.value = picked.der.name;
  signPriFileName.value = picked.key.name;
  pickedCertFolderHint.value = picked.pairDir;
  parseDerPreview(picked.der);
};

const onCertFolderChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;
  processFiles(input.files);
  input.value = '';
};

// 인증서 파일 선택 (input change 이벤트)
const onCertFilesChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;
  
  processFiles(input.files);
  
  // input 초기화 (같은 파일 재선택 가능하도록)
  input.value = '';
};

// 인증서 업로드 모달 닫기
const closeCertUploadModal = () => {
  if (certUploading.value) return;
  showCertUploadModal.value = false;
  signCertFile.value = null;
  signPriFile.value = null;
  signCertFileName.value = '';
  signPriFileName.value = '';
  pickedCertFolderHint.value = '';
  certPassword.value = '';
  showCertPassword.value = false;
  extractedCertInfo.value = null;
  certEditMode.value = 'password';
  newCertPassword.value = '';
  showNewCertPassword.value = false;
};

// 인증서 모달 열기 — 만료된 인증서는 전체 변경 탭이 기본
const openCertModal = () => {
  certEditMode.value = certInfo.value && !isExpired.value ? 'password' : 'full';
  showCertUploadModal.value = true;
};

// 비밀번호만 변경
const updateCertPasswordOnly = async () => {
  if (!certInfo.value?.cic_idx || !newCertPassword.value.trim()) {
    return;
  }

  certPasswordUpdating.value = true;
  try {
    await certApi.updatePassword(certInfo.value.cic_idx, newCertPassword.value);
    
    closeCertUploadModal();
    
    snackbarText.value = '비밀번호가 변경되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('비밀번호 변경 오류:', error);
    snackbarText.value = error.response?.data?.message || '비밀번호 변경에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    certPasswordUpdating.value = false;
  }
};

// 인증서 업로드 (NPKI 방식)
const uploadCert = async () => {
  if (!memberData?.value?.m_code || !signCertFile.value || !signPriFile.value || !certPassword.value.trim()) {
    return;
  }

  certUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('mCode', String(memberData.value.m_code));
    formData.append('signCert', signCertFile.value);  // signCert.der
    formData.append('signPri', signPriFile.value);    // signPri.key
    formData.append('password', certPassword.value);  // 인증서 비밀번호
    
    if (extractedCertInfo.value) {
      formData.append('certName', extractedCertInfo.value.subject);
      formData.append('expireDate', extractedCertInfo.value.expireDate);
    }

    await certApi.upload(formData);
    
    // 모달 닫기
    certUploading.value = false;
    showCertUploadModal.value = false;
    signCertFile.value = null;
    signPriFile.value = null;
    signCertFileName.value = '';
    signPriFileName.value = '';
    pickedCertFolderHint.value = '';
    certPassword.value = '';
    showCertPassword.value = false;
    extractedCertInfo.value = null;
    
    await loadCertInfo();
    
    snackbarText.value = '인증서가 등록되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('인증서 업로드 오류:', error);
    snackbarText.value = error.response?.data?.message || '인증서 업로드에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    certUploading.value = false;
  }
};

// 인증서 삭제 확인
const confirmDeleteCert = () => {
  showDeleteCertDialog.value = true;
};

// 인증서 삭제
const deleteCert = async () => {
  if (!certInfo.value?.cic_idx) return;

  certDeleting.value = true;
  try {
    await certApi.delete(certInfo.value.cic_idx);
    
    showDeleteCertDialog.value = false;
    certInfo.value = null;
    
    snackbarText.value = '인증서가 삭제되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    console.error('인증서 삭제 오류:', error);
    snackbarText.value = error.response?.data?.message || '인증서 삭제에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    certDeleting.value = false;
  }
};

// ========== IP 관리 함수 (슈퍼바이저 전용) ==========

const toggleIpManagement = () => {
  showIpManagement.value = !showIpManagement.value;
  if (showIpManagement.value && certInfo.value?.cic_idx) {
    loadAllowedIPs();
    loadMyIP();
  }
};

const loadAllowedIPs = async () => {
  if (!certInfo.value?.cic_idx) return;
  
  loadingIPs.value = true;
  try {
    const res = await certApi.getAllowedIPs(certInfo.value.cic_idx);
    allowedIPs.value = res.data.data || [];
  } catch (error) {
    console.error('허용 IP 목록 조회 실패:', error);
    allowedIPs.value = [];
  } finally {
    loadingIPs.value = false;
  }
};

const loadMyIP = async () => {
  try {
    const res = await certApi.getMyIP();
    myIP.value = res.data.data?.ip || '';
  } catch (error) {
    console.error('IP 조회 실패:', error);
  }
};

const openAddIpDialog = () => {
  newIP.value = { ip: '', description: '' };
  showAddIpDialog.value = true;
};

const applyMyIP = () => {
  if (myIP.value) {
    newIP.value.ip = myIP.value;
  }
};

const addAllowedIP = async () => {
  if (!newIP.value.ip || !certInfo.value?.cic_idx) return;

  addingIP.value = true;
  try {
    await certApi.addAllowedIP(certInfo.value.cic_idx, {
      ip: newIP.value.ip.trim(),
      description: newIP.value.description.trim()
    });
    showAddIpDialog.value = false;
    loadAllowedIPs();
    snackbarText.value = 'IP가 추가되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    const msg = error.response?.data?.message || 'IP 추가에 실패했습니다.';
    snackbarText.value = msg;
    snackbarColor.value = 'error';
    snackbar.value = true;
  } finally {
    addingIP.value = false;
  }
};

const deleteAllowedIP = async (ip: any) => {
  if (!confirm(`IP ${ip.ccai_ip}를 삭제하시겠습니까?`)) return;

  try {
    await certApi.deleteAllowedIP(ip.ccai_idx);
    loadAllowedIPs();
    snackbarText.value = 'IP가 삭제되었습니다.';
    snackbarColor.value = 'success';
    snackbar.value = true;
  } catch (error: any) {
    snackbarText.value = 'IP 삭제에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  }
};

const toggleAllowedIP = async (ip: any, isActive: boolean) => {
  try {
    await certApi.toggleAllowedIP(ip.ccai_idx, isActive);
    ip.ccai_is_active = isActive ? 1 : 0;
  } catch (error: any) {
    snackbarText.value = '상태 변경에 실패했습니다.';
    snackbarColor.value = 'error';
    snackbar.value = true;
  }
};

onMounted(() => {
  loadBizGubunOptions();
  loadCertInfo();
  if (memberData?.value?.m_longterm_code) {
    checkCrawlData(memberData.value.m_longterm_code);
  }
});
</script>
